import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { Subject, takeUntil } from 'rxjs';

import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { LtiSetupWizardComponent } from '../../../components/dialogs/lti-setup-wizard/lti-setup-wizard.component';
import {
  LmsRegistration,
  LmsRegistryService,
} from '../../../services/lms-registry.service';

/**
 * LTI 1.3 Integration page (mounted at `/integrations/lti`).
 *
 * Single purpose: list every LMS that has been wired up to embed the
 * Alphabees chat widget via LTI 1.3, plus a button to add another via
 * the {@link LtiSetupWizardComponent}.
 *
 * Moodle Block Plugin sites do *not* show up here — those self-register
 * through the signed inbound API (`POST /v1/moodle/register`) and are
 * managed in the Plugin track, not in this admin UI.
 */
@Component({
  selector: 'app-lms-registrations',
  templateUrl: './lms-registrations.component.html',
  styleUrl: './lms-registrations.component.scss',
})
export class LmsRegistrationsComponent implements OnInit, OnDestroy {
  registrations: LmsRegistration[] = [];
  loading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private lmsRegistry: LmsRegistryService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.loading = true;
    this.lmsRegistry
      .listRegistrations('lti13')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.registrations = rows;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /** Open the LTI 1.3 setup wizard. We reload the registrations list
   *  on close so the new entry shows up immediately. */
  openLtiWizard(): void {
    this.dialogService
      .open(LtiSetupWizardComponent, {
        hasScroll: true,
        closeOnBackdropClick: false,
      })
      .onClose.subscribe(() => this.reload());
  }

  deleteRegistration(reg: LmsRegistration): void {
    this.dialogService
      .open(ConfirmDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
        context: {
          title: 'dialogs.confirmDialog.deleteLmsRegistration.title',
          message: 'dialogs.confirmDialog.deleteLmsRegistration.message',
        },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.lmsRegistry
          .deleteRegistration(reg.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.reload());
      });
  }
}
